import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import ServiceToggle from './ServiceToggle';
import { 
  CheckCircle, Clock, Star, ShoppingCart, UserPlus, AlertTriangle,
  FileText, Bell, Loader2, BarChart3, MessageSquare, Phone, Calendar,
  Zap, Users, Mail, DollarSign, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import apiV2, { orgApi } from '../utils/apiV2';
import TabPageLayout from './TabPageLayout';
import AutoSaveIndicator from './AutoSaveIndicator';

// Official WhatsApp glyph (lucide has no brand icons)
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-label="WhatsApp">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// SWR fetcher
const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

/**
 * NotificationsTab - Shows email and SMS notification toggles
 * Auto-saves on toggle, preset times
 */
const NotificationsTab = () => {
  const [savingKey, setSavingKey] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  
  // Settings state - includes SMS toggles for high-impact notifications
  const [settings, setSettings] = useState({
    // Customer - Email
    reminderEnabled: true,        // 24h reminder before trip
    thankYouEnabled: true,        // Thank you after trip
    abandonedCartEnabled: true,   // Cart recovery
    serviceRecsEnabled: true,     // Service recommendations (cart + post-trip)
    balanceReminderEnabled: true, // 7/3/1-day "you still owe $X" balance reminder
    // Customer - SMS (high-impact only)
    confirmationSmsEnabled: true, // Booking confirmation via SMS
    reminderSmsEnabled: false,    // 24h reminder via SMS
    cancellationSmsEnabled: false, // Cancellation alerts via SMS
    balanceReminderSmsEnabled: false, // Balance-due reminder via SMS (opt-in)
    // Operator
    newBookingEnabled: true,      // New booking alert email
    newBookingSmsEnabled: true,   // New booking alert SMS (default ON)
    newLeadEnabled: true,         // New lead alert
    newLeadSmsEnabled: true,      // New lead alert via SMS to owner (opt-out)
    weeklyReportEnabled: true,    // Weekly performance report
    // Operator digest
    upcomingBookingsEnabled: true,    // Tomorrow's bookings digest email
    upcomingBookingsSmsEnabled: false, // Tomorrow's bookings digest SMS
    // Lead capture (embedded inquiry form + alert routing)
    inquiryLeadDelayMinutes: 0,       // Instant alerts for inquiry-form leads
    newLeadDelayMinutes: 10,          // Delay for abandoned-checkout leads
    leadAlertRecipients: [],          // [{name,email,phone}] — empty = owner
    leadFollowUpEnabled: false,       // 2nd customer touch w/ discount code
    leadFollowUpDelayHours: 24,
    leadFollowUpDiscountCode: '',
  });

  // AoC is temporarily paused platform-wide (Assignments tab hidden from nav)

  // Fetch org data for notification settings
  const { data: orgData, isLoading, mutate } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  const orgId = orgData?.id || orgData?.organizationId;

  // Team members — for the "who gets lead alerts" picker (best-effort; hidden on 403)
  const { data: teamData } = useSWR(
    orgId ? 'lead-alert-team-members' : null,
    async () => {
      try {
        const r = await orgApi.getTeamMembers();
        return r.data;
      } catch {
        return { teamMembers: [] };
      }
    },
    { revalidateOnFocus: false }
  );
  const teamMembers = (teamData?.teamMembers || []).filter(m => m.isActive !== false && (m.email || m.phone));

  const toggleRecipient = (member) => {
    const list = settings.leadAlertRecipients || [];
    const exists = list.some(r => r.email === member.email);
    const next = exists
      ? list.filter(r => r.email !== member.email)
      : [...list, { name: member.name, email: member.email, phone: member.phone }];
    handleToggle('leadAlertRecipients', next);
  };

  // UAE companies: SMS is disabled platform-wide, WhatsApp is the SMS channel
  const isUAE =
    ['uae', 'united arab emirates', 'ae'].includes((orgData?.country || '').trim().toLowerCase()) ||
    (orgData?.currency || '').trim().toUpperCase() === 'AED';

  // Load settings from org data
  useEffect(() => {
    // Try to get notification settings from multiple possible locations
    const ns = orgData?.settings?.notificationSettings || orgData?.notificationSettings || {};
    if (Object.keys(ns).length > 0 || orgData) {
      const uae =
        ['uae', 'united arab emirates', 'ae'].includes((orgData?.country || '').trim().toLowerCase()) ||
        (orgData?.currency || '').trim().toUpperCase() === 'AED';
      setSettings({
        reminderEnabled: ns.reminderEmailEnabled ?? true,
        thankYouEnabled: ns.thankYouEmailEnabled ?? true,
        abandonedCartEnabled: ns.abandonedCartEmailEnabled ?? true,
        serviceRecsEnabled: ns.serviceRecsEnabled ?? true,
        balanceReminderEnabled: ns.balanceReminderEmailEnabled ?? true,
        balanceReminderSmsEnabled: uae ? false : (ns.balanceReminderSmsEnabled ?? true),
        confirmationSmsEnabled: !uae,
        reminderSmsEnabled: uae ? false : (ns.reminderSmsEnabled ?? false),
        cancellationSmsEnabled: !uae,
        newBookingEnabled: ns.newBookingEmailEnabled ?? true,
        newBookingSmsEnabled: uae ? false : (ns.newBookingSmsEnabled ?? true),
        newLeadEnabled: ns.newLeadEmailEnabled ?? true,
        newLeadSmsEnabled: uae ? false : (ns.newLeadSmsEnabled ?? true),
        weeklyReportEnabled: ns.weeklyReportEmailEnabled ?? true,
        upcomingBookingsEnabled: ns.upcomingBookingsEmailEnabled ?? true,
        upcomingBookingsSmsEnabled: uae ? false : (ns.upcomingBookingsSmsEnabled ?? false),
        // WhatsApp — UAE: confirmation + cancellation always on (required)
        confirmationWhatsappEnabled: uae ? true : (ns.confirmationWhatsappEnabled ?? false),
        cancellationWhatsappEnabled: uae ? true : (ns.cancellationWhatsappEnabled ?? false),
        reminderWhatsappEnabled: ns.reminderWhatsappEnabled ?? uae,
        balanceReminderWhatsappEnabled: ns.balanceReminderWhatsappEnabled ?? uae,
        newBookingWhatsappEnabled: ns.newBookingWhatsappEnabled ?? uae,
        newLeadWhatsappEnabled: ns.newLeadWhatsappEnabled ?? uae,
        upcomingBookingsWhatsappEnabled: ns.upcomingBookingsWhatsappEnabled ?? false,
        // Lead capture
        inquiryLeadDelayMinutes: ns.inquiryLeadDelayMinutes ?? 0,
        newLeadDelayMinutes: ns.newLeadDelayMinutes ?? 10,
        leadAlertRecipients: ns.leadAlertRecipients ?? [],
        leadFollowUpEnabled: ns.leadFollowUpEnabled ?? false,
        leadFollowUpDelayHours: ns.leadFollowUpDelayHours ?? 24,
        leadFollowUpDiscountCode: ns.leadFollowUpDiscountCode ?? '',
      });
    }
  }, [orgData]);

  // Auto-save when toggle changes
  const handleToggle = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSavingKey(key);
    
    try {
      const updatedSettings = { ...settings, [key]: value };
      
      // Build the notification settings object
      const notificationSettings = {
        // Customer notifications - Email
        confirmationEmailEnabled: true,
        reminderEmailEnabled: updatedSettings.reminderEnabled,
        reminderHoursBefore: 24,
        thankYouEmailEnabled: updatedSettings.thankYouEnabled,
        thankYouDelayHours: 24,
        thankYouIncludeReview: true,
        abandonedCartEmailEnabled: updatedSettings.abandonedCartEnabled,
        abandonedCartDelayMinutes: 30,
        serviceRecsEnabled: updatedSettings.serviceRecsEnabled,
        balanceReminderEmailEnabled: updatedSettings.balanceReminderEnabled,
        balanceReminderSmsEnabled: updatedSettings.balanceReminderSmsEnabled,
        // Customer notifications - SMS (high-impact only)
        confirmationSmsEnabled: updatedSettings.confirmationSmsEnabled,
        reminderSmsEnabled: updatedSettings.reminderSmsEnabled,
        cancellationSmsEnabled: updatedSettings.cancellationSmsEnabled,
        // Operator notifications
        newBookingEmailEnabled: updatedSettings.newBookingEnabled,
        newBookingSmsEnabled: updatedSettings.newBookingSmsEnabled,
        newLeadEmailEnabled: updatedSettings.newLeadEnabled,
        newLeadSmsEnabled: updatedSettings.newLeadSmsEnabled,
        newLeadDelayMinutes: updatedSettings.newLeadDelayMinutes ?? 10,
        inquiryLeadDelayMinutes: updatedSettings.inquiryLeadDelayMinutes ?? 0,
        leadAlertRecipients: updatedSettings.leadAlertRecipients ?? [],
        leadFollowUpEnabled: updatedSettings.leadFollowUpEnabled ?? false,
        leadFollowUpDelayHours: updatedSettings.leadFollowUpDelayHours ?? 24,
        leadFollowUpDiscountCode: updatedSettings.leadFollowUpDiscountCode ?? '',
        weeklyReportEmailEnabled: updatedSettings.weeklyReportEnabled,
        // Operator upcoming bookings digest
        upcomingBookingsEmailEnabled: updatedSettings.upcomingBookingsEnabled,
        upcomingBookingsSmsEnabled: updatedSettings.upcomingBookingsSmsEnabled,
        // WhatsApp (customer + operator)
        confirmationWhatsappEnabled: updatedSettings.confirmationWhatsappEnabled,
        cancellationWhatsappEnabled: updatedSettings.cancellationWhatsappEnabled,
        reminderWhatsappEnabled: updatedSettings.reminderWhatsappEnabled,
        balanceReminderWhatsappEnabled: updatedSettings.balanceReminderWhatsappEnabled,
        newBookingWhatsappEnabled: updatedSettings.newBookingWhatsappEnabled,
        newLeadWhatsappEnabled: updatedSettings.newLeadWhatsappEnabled,
        upcomingBookingsWhatsappEnabled: updatedSettings.upcomingBookingsWhatsappEnabled,
        // Keep other settings at their defaults
        cancellationEmailEnabled: true,
        reviewRequestEmailEnabled: false,
        reviewRequestSmsEnabled: false,
      };
      
      await apiV2.patch('/auth/organizations/me', {
        // Send under settings.notificationSettings for proper backend storage
        settings: {
          notificationSettings: notificationSettings
        },
        // Also send top-level fields for backwards compatibility
        thankYouEmailEnabled: updatedSettings.thankYouEnabled,
        thankYouEmailDelayHours: 24,
        thankYouEmailIncludeReview: true,
      });

      mutate();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
      
      // Show success toast for SMS toggles
      if (key === 'confirmationSmsEnabled' || key === 'reminderSmsEnabled' || key === 'cancellationSmsEnabled') {
        toast.success(value ? 'SMS notifications enabled' : 'SMS notifications disabled');
      }
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: !value }));
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  // Email-only notification row
  const NotificationRow = ({ icon: Icon, title, subtitle, settingKey, required = false }) => {
    const isEnabled = settings[settingKey];
    const isSaving = savingKey === settingKey;
    return (
      <div className="py-3.5 border-b border-gray-50 last:border-0">
        <div className="flex items-start gap-3">
          <div className="p-1.5 rounded-lg bg-gray-50 flex-shrink-0 mt-0.5">
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="font-medium text-gray-900 text-sm">{title}</span>
                {required && (
                  <span className="ml-2 text-[10px] font-semibold bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded uppercase tracking-wide">Required</span>
                )}
                {subtitle && <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{subtitle}</p>}
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                <Mail className="w-3.5 h-3.5 text-gray-300" />
                {isSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
                {required
                  ? <ServiceToggle checked={true} onChange={() => {}} disabled />
                  : <ServiceToggle checked={!!isEnabled} onChange={(v) => handleToggle(settingKey, v)} data-testid={`notif-${settingKey}-toggle`} />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Email + SMS + WhatsApp notification row. SMS column hidden for UAE orgs.
  const NotificationRowWithSms = ({ icon: Icon, title, subtitle, emailKey, smsKey, whatsappKey, emailRequired = false, smsRequired = false, whatsappRequired = false }) => {
    const emailEnabled = settings[emailKey];
    const smsEnabled = settings[smsKey];
    const waEnabled = settings[whatsappKey];
    const isSavingEmail = savingKey === emailKey;
    const isSavingSms = savingKey === smsKey;
    const isSavingWa = savingKey === whatsappKey;
    return (
      <div className="py-3.5 border-b border-gray-50 last:border-0">
        <div className="flex items-start gap-3">
          <div className="p-1.5 rounded-lg bg-gray-50 flex-shrink-0 mt-0.5">
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <span className="font-medium text-gray-900 text-sm">{title}</span>
                {subtitle && <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{subtitle}</p>}
              </div>
              {/* Toggles — stacked on mobile, inline on desktop */}
              <div className="flex items-center gap-4 flex-shrink-0 pt-0.5">
                {/* Email */}
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-gray-300" />
                  {isSavingEmail && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
                  {emailRequired
                    ? <ServiceToggle checked={true} onChange={() => {}} disabled />
                    : <ServiceToggle checked={!!emailEnabled} onChange={(v) => handleToggle(emailKey, v)} data-testid={`notif-${emailKey}-email-toggle`} />
                  }
                </div>
                {/* SMS — hidden for UAE (SMS disabled platform-wide) */}
                {!isUAE && smsKey && (
                  <div className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-gray-300" />
                    {isSavingSms && <Loader2 className="w-3.5 h-3.5 animate-spin text-green-400" />}
                    {smsRequired
                      ? <ServiceToggle checked={true} onChange={() => {}} disabled data-testid={`notif-${smsKey}-sms-toggle`} />
                      : <ServiceToggle checked={!!smsEnabled} onChange={(v) => handleToggle(smsKey, v)} data-testid={`notif-${smsKey}-sms-toggle`} />
                    }
                  </div>
                )}
                {/* WhatsApp */}
                {whatsappKey && (
                  <div className="flex items-center gap-1.5">
                    <WhatsAppIcon className="w-3.5 h-3.5 text-gray-300" />
                    {isSavingWa && <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-400" />}
                    {whatsappRequired
                      ? <ServiceToggle checked={true} onChange={() => {}} disabled data-testid={`notif-${whatsappKey}-whatsapp-toggle`} />
                      : <ServiceToggle checked={!!waEnabled} onChange={(v) => handleToggle(whatsappKey, v)} data-testid={`notif-${whatsappKey}-whatsapp-toggle`} />
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <TabPageLayout
      title="Notifications"
      subtitle="Control what emails, texts and WhatsApp messages are sent"
      loading={isLoading}
      actions={<AutoSaveIndicator saving={!!savingKey} savedFlash={savedFlash} />}
    >
      <div className="space-y-6">
        
        {/* Customer Emails */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-blue-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Customer Notifications</h3>
            </div>
            {/* Legend */}
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</span>
              {!isUAE && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> SMS</span>}
              <span className="flex items-center gap-1"><WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp</span>
            </div>
          </div>
          
          <NotificationRowWithSms
            icon={CheckCircle}
            title="Booking Confirmation"
            subtitle="Sent immediately when booked"
            emailKey="confirmationEnabled"
            smsKey="confirmationSmsEnabled"
            whatsappKey="confirmationWhatsappEnabled"
            emailRequired={true}
            smsRequired={true}
            whatsappRequired={isUAE}
          />
          
          <NotificationRowWithSms
            icon={AlertTriangle}
            title="Cancellation Alert"
            subtitle="When booking is cancelled"
            emailKey="cancellationEnabled"
            smsKey="cancellationSmsEnabled"
            whatsappKey="cancellationWhatsappEnabled"
            emailRequired={true}
            smsRequired={true}
            whatsappRequired={isUAE}
          />
          
          <NotificationRow
            icon={FileText}
            title="Waiver Request"
            subtitle="Included with confirmation"
            settingKey="waiverEnabled"
            required={true}
          />
          
          <NotificationRowWithSms
            icon={Clock}
            title="Trip Reminder"
            subtitle="24 hours before"
            emailKey="reminderEnabled"
            smsKey="reminderSmsEnabled"
            whatsappKey="reminderWhatsappEnabled"
          />
          
          <NotificationRow
            icon={Star}
            title="Thank You"
            subtitle="24 hours after trip"
            settingKey="thankYouEnabled"
          />

          <NotificationRowWithSms
            icon={DollarSign}
            title="Balance Due Reminder"
            subtitle={'7, 3 & 1 days before — "you still owe $X" with a one-tap pay link'}
            emailKey="balanceReminderEnabled"
            smsKey="balanceReminderSmsEnabled"
            whatsappKey="balanceReminderWhatsappEnabled"
          />
          
          <NotificationRow
            icon={ShoppingCart}
            title="Abandoned Cart"
            subtitle="30 min after leaving checkout"
            settingKey="abandonedCartEnabled"
          />
          
          <NotificationRow
            icon={Sparkles}
            title="Service Recommendations"
            subtitle={'Gentle "you might also like" picks in cart-recovery emails, plus a one-time BookingFlows follow-up the day after a trip. Needs at least 2 other live services.'}
            settingKey="serviceRecsEnabled"
          />
        </div>

        {/* Your Alerts */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4 sm:p-6">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-green-600" />
              <h3 className="font-semibold text-gray-900 text-sm">Your Alerts</h3>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Email</span>
              {!isUAE && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> SMS</span>}
              <span className="flex items-center gap-1"><WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp</span>
            </div>
          </div>
          
          <NotificationRowWithSms
            icon={CheckCircle}
            title="New Booking"
            subtitle="Instant alert when someone books"
            emailKey="newBookingEnabled"
            smsKey="newBookingSmsEnabled"
            whatsappKey="newBookingWhatsappEnabled"
          />
          
          <NotificationRowWithSms
            icon={UserPlus}
            title="New Lead"
            subtitle="Name, interest, phone & email — timing & recipients configurable in Lead Capture below"
            emailKey="newLeadEnabled"
            smsKey="newLeadSmsEnabled"
            whatsappKey="newLeadWhatsappEnabled"
          />
          
          <NotificationRow
            icon={BarChart3}
            title="Weekly Report"
            subtitle="Performance summary every Monday"
            settingKey="weeklyReportEnabled"
          />

          <NotificationRowWithSms
            icon={Calendar}
            title="Upcoming Bookings Digest"
            subtitle="Evening heads-up when you have bookings the next day"
            emailKey="upcomingBookingsEnabled"
            smsKey="upcomingBookingsSmsEnabled"
            whatsappKey="upcomingBookingsWhatsappEnabled"
          />
        </div>

        {/* Lead Capture — inquiry form alert routing + follow-up offers */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-4 sm:p-6" data-testid="lead-capture-settings-card">
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-orange-500" />
              <h3 className="font-semibold text-gray-900 text-sm">Lead Capture</h3>
            </div>
            <span className="text-xs text-gray-400">Alert timing · recipients · follow-ups</span>
          </div>

          {/* Alert timing */}
          <div className="py-3.5 border-b border-gray-50">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-gray-50 flex-shrink-0 mt-0.5"><Clock className="w-4 h-4 text-gray-400" /></div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-900 text-sm">Alert timing</span>
                <p className="text-xs text-gray-400 mt-0.5">How fast your team hears about each kind of lead</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Inquiry form leads</label>
                    <select
                      value={settings.inquiryLeadDelayMinutes ?? 0}
                      onChange={(e) => handleToggle('inquiryLeadDelayMinutes', Number(e.target.value))}
                      data-testid="lead-inquiry-delay-select"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value={0}>Instant (recommended)</option>
                      <option value={5}>After 5 minutes</option>
                      <option value={10}>After 10 minutes</option>
                      <option value={15}>After 15 minutes</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Abandoned checkout leads</label>
                    <select
                      value={settings.newLeadDelayMinutes ?? 10}
                      onChange={(e) => handleToggle('newLeadDelayMinutes', Number(e.target.value))}
                      data-testid="lead-checkout-delay-select"
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value={5}>After 5 minutes</option>
                      <option value={10}>After 10 minutes (recommended)</option>
                      <option value={15}>After 15 minutes</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recipients */}
          <div className="py-3.5 border-b border-gray-50">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-gray-50 flex-shrink-0 mt-0.5"><Users className="w-4 h-4 text-gray-400" /></div>
              <div className="flex-1 min-w-0">
                <span className="font-medium text-gray-900 text-sm">Who gets lead alerts</span>
                <p className="text-xs text-gray-400 mt-0.5" data-testid="lead-recipients-summary">
                  {(settings.leadAlertRecipients || []).length === 0
                    ? 'Currently going to the owner — pick team members to route alerts to them instead'
                    : `${settings.leadAlertRecipients.length} team member${settings.leadAlertRecipients.length === 1 ? '' : 's'} selected`}
                </p>
                {teamMembers.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {teamMembers.map((m) => {
                      const selected = (settings.leadAlertRecipients || []).some(r => r.email === m.email);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => toggleRecipient(m)}
                          data-testid={`lead-recipient-${m.id}`}
                          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${selected ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        >
                          {m.name}{m.isOwnerMember ? ' (owner)' : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Follow-up offer */}
          <div className="py-3.5">
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded-lg bg-gray-50 flex-shrink-0 mt-0.5"><Sparkles className="w-4 h-4 text-gray-400" /></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <span className="font-medium text-gray-900 text-sm">Follow-up offer</span>
                    <p className="text-xs text-gray-400 mt-0.5">If a lead still hasn't booked, automatically email them a discount code</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 pt-0.5">
                    {savingKey === 'leadFollowUpEnabled' && <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-400" />}
                    <ServiceToggle checked={!!settings.leadFollowUpEnabled} onChange={(v) => handleToggle('leadFollowUpEnabled', v)} data-testid="lead-followup-toggle" />
                  </div>
                </div>
                {settings.leadFollowUpEnabled && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Discount code</label>
                      <input
                        type="text"
                        value={settings.leadFollowUpDiscountCode || ''}
                        onChange={(e) => setSettings(prev => ({ ...prev, leadFollowUpDiscountCode: e.target.value.toUpperCase() }))}
                        onBlur={(e) => handleToggle('leadFollowUpDiscountCode', e.target.value.trim().toUpperCase())}
                        placeholder="e.g. COMEBACK10"
                        data-testid="lead-followup-code-input"
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100 font-mono"
                      />
                      <p className="text-[11px] text-gray-400 mt-1">Use an active promo code (Deck → Promo Codes) so it works at checkout.</p>
                    </div>
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Send after</label>
                      <select
                        value={settings.leadFollowUpDelayHours ?? 24}
                        onChange={(e) => handleToggle('leadFollowUpDelayHours', Number(e.target.value))}
                        data-testid="lead-followup-delay-select"
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                      >
                        <option value={6}>6 hours</option>
                        <option value={12}>12 hours</option>
                        <option value={24}>24 hours</option>
                        <option value={48}>48 hours</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── AoC — Assignment on Confirmation ─────────────────────────────── */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-600" />
            <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Assignments</h2>
          </div>

          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="p-4 flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">Self-assignment for team members</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Temporarily unavailable — reach out via the <a href="/support#tickets" className="text-blue-600 hover:underline">Support Center</a> if you'd like self-assignment for your team.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[
                      'Role-matched — only eligible team see each trip',
                      '30-min claim window',
                      'Multi-role: Captain + Crew claim independently',
                    ].map(t => (
                      <span key={t} className="text-[11px] text-gray-400 bg-gray-50 px-2 py-0.5 rounded-full border border-gray-100">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex-shrink-0 mt-0.5 opacity-50" title="Temporarily unavailable">
                <ServiceToggle
                  checked={false}
                  onChange={() => {}}
                  disabled
                  data-testid="aoc-toggle"
                />
              </div>
            </div>
          </div>
        </div>

      </div>
    </TabPageLayout>
  );
};

export default NotificationsTab;
